import React, { useState, useEffect } from "react";
import {
  makeStyles,
  tokens,
  Button,
  Input,
  Label,
  Link,
  MessageBar,
  MessageBarBody,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
} from "@fluentui/react-components";
import useAuthSignIn from "./UseAuthsignIn";
import useSsoSignIn from "./useSsoSignIn";
import { isEntraConfigured, isAuth0Configured } from "../../helper/authConfig";
import AuthState from "./AuthState";
import axios from "axios";
import { apiURL } from "../../helper/urls";
import useShowWelcome from "../../hooks/useShowWelcome";

const useStyles = makeStyles({
  page: {
    display: "flex",
    height: "100vh",
    width: "100%",
    flexDirection: "column",
    padding: "24px",
  },
  header: {
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
    fontSize: "13px",
    lineHeight: "20px",
    marginBottom: "24px",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    flex: 1,
    justifyContent: "center",
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  loginButton: {
    marginTop: "8px",
    backgroundColor: "#262626",
    color: "#ffffff",
    ":hover": {
      backgroundColor: "#404040",
      color: "#ffffff",
    },
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "4px",
    fontSize: "12px",
  },
  forgotLink: {
    cursor: "pointer",
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    ":hover": {
      color: tokens.colorNeutralForeground1,
    },
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "12px",
    marginBottom: "4px",
  },
  dividerLine: {
    flex: 1,
    height: "1px",
    backgroundColor: tokens.colorNeutralStroke2,
  },
  dividerText: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
  },
  ssoButton: {
    width: "100%",
    marginTop: "8px",
  },
});

const SignInComponent: React.FC = () => {
  const styles = useStyles();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const { submitSignIn, isLoading } = useAuthSignIn();
  const { startSso, activeProvider, error: ssoError } = useSsoSignIn();
  const [message, setMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const { setShowWelcome } = useShowWelcome();

  // Surface SSO errors in the same message bar the password flow uses, and
  // clear a stale error banner when a new attempt resets ssoError to null.
  useEffect(() => {
    if (ssoError) {
      setMessage({ text: ssoError, type: "error" });
    } else {
      setMessage((current) => (current?.type === "error" ? null : current));
    }
  }, [ssoError]);

  const onSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!formData.email || !formData.password) {
      setMessage({ text: "Username and Password are required", type: "error" });
      return;
    }

    try {
      setShowWelcome(true);
      const { success, message: msg } = await submitSignIn(formData);
      setMessage({
        text: success ? msg : "Incorrect Username or Password",
        type: success ? "success" : "error",
      });
    } catch {
      setMessage({ text: "Incorrect Username or Password", type: "error" });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") onSubmit();
  };

  const handleForgotPassword = async () => {
    try {
      await axios.post(apiURL("forgot_password"), { email: forgotEmail });
      setMessage({ text: "Password reset email sent successfully", type: "success" });
    } catch {
      setMessage({ text: "Failed to send password reset email.", type: "error" });
    } finally {
      setForgotOpen(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        This add-in connects to{" "}
        <Link href="https://www.mytender.io" target="_blank" inline>
          mytender.io
        </Link>{" "}
        to help you win tenders using artificial intelligence.
      </div>

      <div className={styles.card}>
        <div className={styles.title}>Login</div>

        <div className={styles.field}>
          <Label htmlFor="email-input" size="small">
            Username
          </Label>
          <Input
            id="email-input"
            value={formData.email}
            onChange={(_, data) => setFormData({ ...formData, email: data.value })}
            onKeyDown={handleKeyDown}
            autoComplete="username"
            size="medium"
          />
        </div>

        <div className={styles.field}>
          <Label htmlFor="password-input" size="small">
            Password
          </Label>
          <Input
            id="password-input"
            type="password"
            value={formData.password}
            onChange={(_, data) => setFormData({ ...formData, password: data.value })}
            onKeyDown={handleKeyDown}
            autoComplete="current-password"
            size="medium"
          />
        </div>

        <Button className={styles.loginButton} appearance="primary" onClick={onSubmit} disabled={isLoading}>
          {isLoading ? "Loading..." : "Login"}
        </Button>

        <div className={styles.footer}>
          <Dialog open={forgotOpen} onOpenChange={(_, data) => setForgotOpen(data.open)}>
            <DialogTrigger disableButtonEnhancement>
              <span className={styles.forgotLink}>Forgot your password?</span>
            </DialogTrigger>
            <DialogSurface>
              <DialogBody>
                <DialogTitle>Forgot Password</DialogTitle>
                <DialogContent>
                  <p style={{ margin: "8px 0 16px", fontSize: "13px", color: tokens.colorNeutralForeground3 }}>
                    Enter your email and we&apos;ll send a reset link.
                  </p>
                  <Input
                    type="email"
                    value={forgotEmail}
                    onChange={(_, data) => setForgotEmail(data.value)}
                    placeholder="Email address"
                    style={{ width: "100%" }}
                  />
                </DialogContent>
                <DialogActions>
                  <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">Cancel</Button>
                  </DialogTrigger>
                  <Button appearance="primary" onClick={handleForgotPassword}>
                    Send Email
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
          <Link href="https://www.mytender.io" target="_blank" inline>
            Sign up
          </Link>
        </div>

        {(isAuth0Configured() || isEntraConfigured()) && (
          <>
            <div className={styles.divider}>
              <span className={styles.dividerLine} />
              <span className={styles.dividerText}>or continue with</span>
              <span className={styles.dividerLine} />
            </div>

            {isAuth0Configured() && (
              <Button
                className={styles.ssoButton}
                appearance="outline"
                onClick={() => startSso("auth0")}
                disabled={activeProvider !== null}
              >
                {activeProvider === "auth0" ? "Loading..." : "Sign in with Okta"}
              </Button>
            )}

            {isEntraConfigured() && (
              <Button
                className={styles.ssoButton}
                appearance="outline"
                onClick={() => startSso("entra")}
                disabled={activeProvider !== null}
              >
                {activeProvider === "entra" ? "Loading..." : "Sign in with Microsoft"}
              </Button>
            )}
          </>
        )}

        {message && (
          <MessageBar intent={message.type === "success" ? "success" : "error"} style={{ marginTop: "8px" }}>
            <MessageBarBody>{message.text}</MessageBarBody>
          </MessageBar>
        )}

        <AuthState />
      </div>
    </div>
  );
};

export default SignInComponent;
